import { OrderBy, type BaseFilterBy } from '@customTypes/cloudmix/api'
import { hydrateListSearchBeforeLoad } from '@lib/listState/hydrateListSearchBeforeLoad'
import type { ListStateService } from '@lib/listState/service'
import type { ListSearchFields } from '@lib/listState/queryState'

type ListBeforeLoadArgs<
  TParams extends Record<string, string>,
  TSearch extends ListSearchFields,
> = {
  context: {
    listState: ListStateService
  }
  routeId: string
  cause: 'preload' | 'enter' | 'stay'
  params: TParams
  search: TSearch
}

type ListHydrationConfig<
  TParams extends Record<string, string>,
  F extends Record<string, unknown>,
> = {
  defaultSort?: OrderBy[]
  defaultFilterBy?: F
  allowedOrderByFields?: readonly string[]
  getScope?: (params: TParams) => string | undefined
}

type WithListHydrationOptions<
  TParams extends Record<string, string>,
  TSearch extends ListSearchFields,
  F extends Record<string, unknown>,
  TResult extends object | void,
> = {
  list: ListHydrationConfig<TParams, F>
  getTitle?: (args: ListBeforeLoadArgs<TParams, TSearch>) => string
  beforeLoad?: (
    args: ListBeforeLoadArgs<TParams, TSearch>
  ) => TResult | Promise<TResult>
}

function getOrgId<TParams extends Record<string, string>>(params: TParams) {
  return 'orgId' in params ? params.orgId : undefined
}

export function withListHydration<
  TParams extends Record<string, string> = Record<string, string>,
  TSearch extends ListSearchFields = ListSearchFields,
  F extends Record<string, unknown> = BaseFilterBy,
  TResult extends object | void = object | void,
>(options: WithListHydrationOptions<TParams, TSearch, F, TResult>) {
  return async (args: ListBeforeLoadArgs<TParams, TSearch>) => {
    if (args.cause === 'enter') {
      hydrateListSearchBeforeLoad<F, ListSearchFields>({
        search: args.search,
        listState: args.context.listState,
        orgId: getOrgId(args.params),
        routeId: args.routeId,
        scope: options.list.getScope?.(args.params),
        defaultSort: options.list.defaultSort,
        defaultFilterBy: options.list.defaultFilterBy,
        allowedOrderByFields: options.list.allowedOrderByFields,
      })
    }

    const baseResult = options.beforeLoad
      ? await options.beforeLoad(args)
      : undefined

    if (!options.getTitle) {
      return baseResult
    }

    return {
      ...(baseResult ?? {}),
      getTitle: () => options.getTitle!(args),
    }
  }
}
